import { useQuery } from '@tanstack/react-query';
import { apiGetQuiet } from '../../shared/api/client';
import { useValidatedBabyId } from '../../shared/hooks/useValidatedBabyId';
import { useSubscription } from './useSubscription';

/**
 * Effective entitlement for the SELECTED BABY — the family-shared plan.
 *
 * The server resolves max(caller's own plan, baby owner's plan) for
 * baby-scoped features, so a paying parent's Premium covers every caregiver
 * on their baby. Use this hook for every baby-scoped gate: the history cap,
 * growth charts, weekly insights, exports, the AI digest, sleep predictions,
 * the calendar feed and sleep notifications.
 *
 * Use `useSubscription()` — the caller's OWN account plan — for purchase,
 * restore and the plan shown on /subscription; a shared plan never widens it.
 *
 * Only free accounts ask the server (`/subscription/status?babyId=`): an own
 * Premium (or preview) already unlocks everything. While the baby-scoped
 * answer is in flight the hook reports `isLoading` rather than falling back
 * to the narrower own plan, so gates don't flash a locked teaser at a
 * caregiver who is entitled. Offline, or without a selected baby, it is
 * exactly the own plan (fail closed).
 *
 * `settled` (v1.5.1, docs/v151-spec.md §6.2) is stricter than `!isLoading`:
 * true only once the own status has answered, the babies list has arrived
 * and — on Free with a baby selected — the baby-scoped answer has too. A
 * failed or offline-paused fetch is not loading (gates fall back to the own
 * plan) but it is never settled. The one-off paywall redirects
 * (useLapsedRedirect, useSecondAsk) wait for it, so a caregiver covered by
 * the owner's Premium is never sent to the paywall on a partial answer.
 */
export function useEntitlement() {
  const own = useSubscription();
  const {
    babies = [],
    selectedBabyId: babyId,
    isFetched,
    isLoading: babiesLoading,
    isSuccess: babiesSuccess,
  } = useValidatedBabyId();

  const needsBabyPlan = !!babyId && !own.isLoading && own.plan !== 'premium';

  // Quiet fetch on purpose: a stale babyId (caregiver just removed, baby just
  // deleted) 403s here until useValidatedBabyId reselects, and the standard
  // client would toast "Access denied" for it. On any error the hook simply
  // falls back to the own plan below.
  const query = useQuery({
    queryKey: ['entitlement', babyId],
    queryFn: () => apiGetQuiet('/subscription/status', { babyId }),
    enabled: needsBabyPlan,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => failureCount < 1 && err?.status !== 403,
  });

  const ent = needsBabyPlan ? query.data?.entitlement : null;
  const rawPlan = ent?.plan || own.plan;
  const plan = rawPlan === 'plus' ? 'premium' : rawPlan;
  const limits = ent?.limits || own.limits;
  const sharedByOwner = !!ent?.sharedByOwner;
  // Babies listed but none selected yet: useValidatedBabyId picks one in its
  // effect, so for a render the baby-scoped answer has not even been asked
  // for. That is loading, not the narrower own plan.
  const reconciling = !!isFetched && babies.length > 0 && !babyId;
  const isLoading = own.isLoading || babiesLoading || reconciling || (needsBabyPlan && query.isLoading);
  // `hasStatus` arrived with v1.5.1's useSubscription; anything but true
  // means the own status has not answered.
  const settled =
    own.hasStatus === true &&
    !!babiesSuccess &&
    (babies.length === 0 || (!!babyId && (!needsBabyPlan || query.isSuccess)));

  function hasFeature(feature) {
    return limits?.features?.includes(feature) || false;
  }

  return {
    babyId,
    plan,
    limits,
    sharedByOwner,
    hasFeature,
    isLoading,
    settled,
    refetch: query.refetch,
  };
}
